// src/services/documentsService.js
//
// Frontend client for "Documents Received on Whatsapp" upload flow.
//
// Backend endpoints expected (Laravel side — see notes below):
//   GET    /api/complaints/{queryId}/documents
//   POST   /api/complaints/{queryId}/documents               (multipart/form-data)
//   GET    /api/complaints/{queryId}/documents/{documentId}/download
//   DELETE /api/complaints/{queryId}/documents/{documentId}
//
// Expected response shapes:
//   GET list -> { documents: [{ id, fileName, mimeType, size, documentType, notes,
//                                uploadedAt, uploadedBy, url }] }
//   POST     -> { documents: [...newly uploaded items as above...] }
//
// Backend should accept multipart fields:
//   - files[]      (one or more files)
//   - documentType (string)
//   - notes        (string, optional)
//   - uploadedBy   (string, optional - sent for audit if available)
//
// If the backend prefers different keys, adjust buildFormData() below.

import api from './api.js';

const encodeId = (value) => encodeURIComponent(String(value ?? '').trim());

export const ALLOWED_DOC_EXTENSIONS = ['pdf', 'jpg', 'jpeg', 'png', 'doc', 'docx'];
export const ALLOWED_DOC_MIME_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/jpg',
  'image/png',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];
export const DEFAULT_MAX_DOC_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB

export const getFileExtension = (fileName = '') => {
  const idx = String(fileName).lastIndexOf('.');
  if (idx < 0) return '';
  return String(fileName).slice(idx + 1).toLowerCase();
};

export const isAllowedDocFile = (file) => {
  if (!file) return false;
  const ext = getFileExtension(file.name);
  if (ALLOWED_DOC_EXTENSIONS.includes(ext)) return true;
  if (file.type && ALLOWED_DOC_MIME_TYPES.includes(file.type)) return true;
  return false;
};

export const formatFileSize = (bytes) => {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  let value = n;
  let unitIdx = 0;
  while (value >= 1024 && unitIdx < units.length - 1) {
    value /= 1024;
    unitIdx += 1;
  }
  return `${value.toFixed(value < 10 && unitIdx > 0 ? 1 : 0)} ${units[unitIdx]}`;
};

const buildFormData = ({ files = [], documentType = '', notes = '', uploadedBy = '' } = {}) => {
  const fd = new FormData();
  files.forEach((file) => fd.append('files[]', file, file.name));
  if (documentType) fd.append('documentType', documentType);
  if (notes) fd.append('notes', notes);
  if (uploadedBy) fd.append('uploadedBy', uploadedBy);
  return fd;
};

export async function listDocuments(queryId, { userId = '' } = {}) {
  const id = encodeId(queryId);
  if (!id) throw new Error('queryId is required');
  const params = {};
  if (userId) params.userId = userId;
  const res = await api.get(`/complaints/${id}/documents`, { params });
  const data = res?.data || {};
  return Array.isArray(data.documents) ? data.documents : Array.isArray(data) ? data : [];
}

export async function uploadDocuments(queryId, payload = {}) {
  const id = encodeId(queryId);
  if (!id) throw new Error('queryId is required');
  if (!Array.isArray(payload.files) || payload.files.length === 0) {
    throw new Error('At least one file is required');
  }
  const formData = buildFormData(payload);
  const res = await api.post(`/complaints/${id}/documents`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  const data = res?.data || {};
  return Array.isArray(data.documents) ? data.documents : Array.isArray(data) ? data : [];
}

export function getDocumentDownloadUrl(queryId, documentId, { userId = '', userName = '' } = {}) {
  const id = encodeId(queryId);
  const docId = encodeId(documentId);
  if (!id || !docId) return '';
  const base = (api?.defaults?.baseURL || '').replace(/\/$/, '');
  const qs = new URLSearchParams();
  if (userId) qs.set('userId', userId);
  if (userName) qs.set('userName', userName);
  const query = qs.toString();
  return `${base}/complaints/${id}/documents/${docId}/download${query ? `?${query}` : ''}`;
}

/**
 * Log that the current user viewed (opened) a WhatsApp document.
 * By default only the FIRST view per user is stored — pass force=true to
 * record every view (history-tracking mode).
 */
export async function logDocumentView(queryId, documentId, { userId = '', userName = '', force = false } = {}) {
  const id = encodeId(queryId);
  const docId = encodeId(documentId);
  if (!id || !docId) throw new Error('queryId and documentId are required');
  const res = await api.post(`/complaints/${id}/documents/${docId}/log-view`, {
    userId,
    userName,
    force,
  });
  return res?.data || {};
}

/**
 * Fetch the full activity history (view + download events) for a document.
 */
export async function getDocumentActivity(queryId, documentId) {
  const id = encodeId(queryId);
  const docId = encodeId(documentId);
  if (!id || !docId) throw new Error('queryId and documentId are required');
  const res = await api.get(`/complaints/${id}/documents/${docId}/activity`);
  return res?.data || {};
}

export async function deleteDocument(queryId, documentId) {
  const id = encodeId(queryId);
  const docId = encodeId(documentId);
  if (!id || !docId) throw new Error('queryId and documentId are required');
  await api.delete(`/complaints/${id}/documents/${docId}`);
  return true;
}

const documentsService = {
  ALLOWED_DOC_EXTENSIONS,
  ALLOWED_DOC_MIME_TYPES,
  DEFAULT_MAX_DOC_SIZE_BYTES,
  getFileExtension,
  isAllowedDocFile,
  formatFileSize,
  listDocuments,
  uploadDocuments,
  getDocumentDownloadUrl,
  deleteDocument,
  logDocumentView,
  getDocumentActivity,
};

export default documentsService;
